import { inject, Injectable } from '@angular/core';
import { Role, RoleModel, RoleType, UiRoleType } from 'src/app/services/RoleModel';
import { ConsumptionInfoReceiver, Owner, Person, Resident, Tenant, TenantUnitDetail, Vacancy } from '@eadsysteme/ewsrealestateclient-v3';
import { RoleSetting } from '@eadsysteme/energyefficiencyclient-v1';
import dayjs, { Dayjs, isDayjs } from 'dayjs';
import { DateUtil } from '../../../util/DateUtil';
import { TimePeriod, TimePeriodService } from './time-period.service';
import { SYNTHETIC_NEW_ID_PREFIX, SYNTHETIC_VACANCY_ID_PREFIX } from './roles-view-model';
import RoleTypeEnum = RoleSetting.RoleTypeEnum;

/** Rollen-DTO der Nutzeinheit — alle Rollentypen teilen dieselbe Wire-Struktur. */
export type TenantUnitRoleDto = Owner | Resident | Tenant | Vacancy | ConsumptionInfoReceiver;

export interface RoleFilterConfig {
	showOwner: boolean;
	showResident: boolean;
	showTenant: boolean;
	showVacancy: boolean;
	showConsumptionInfoReceiver?: boolean;
	validFrom?: Date;
	validTo?: Date;
}

@Injectable({
	providedIn: 'root',
})
export class RoleModelService {
	private readonly timePeriodService = inject(TimePeriodService);

	/**
	 * Builds RoleModel array from tenant unit data.
	 *
	 * @param tenantUnit - The tenant unit detail data
	 * @param hideTenants - Whether to hide tenant roles
	 * @returns Object containing role models, gaps, and available periods
	 */
	buildRoleModels(
		tenantUnit: TenantUnitDetail,
		hideTenants: boolean
	): {
		roleModels: RoleModel[];
		ownerTenantGaps: TimePeriod[];
		uncoveredVacancyGaps: TimePeriod[];
		availablePeriodsByRoleType: Map<RoleTypeEnum, TimePeriod[]>;
	} {
		const sortedData = this.getSortedRoleData(tenantUnit, hideTenants);
		const occupiedPeriods = this.buildOccupiedPeriods(sortedData.owners, sortedData.tenants);
		const mergedOccupiedPeriods = this.timePeriodService.mergeTimePeriods(occupiedPeriods);

		const ownerTenantGaps = this.timePeriodService.calculateGapsBetweenPeriods(mergedOccupiedPeriods);
		const vacancyTimePeriods = this.buildVacancyTimePeriods(sortedData.vacancies);
		const uncoveredVacancyGaps = this.timePeriodService.calculateUncoveredGaps(ownerTenantGaps, vacancyTimePeriods);

		const roleModelsMap = this.buildRoleModelsMap(sortedData, uncoveredVacancyGaps);
		const availablePeriodsByRoleType = this.buildAvailablePeriodsByRoleType(tenantUnit, hideTenants);

		return {
			roleModels: Array.from(roleModelsMap.values()),
			ownerTenantGaps,
			uncoveredVacancyGaps,
			availablePeriodsByRoleType,
		};
	}

	/**
	 * Adds a role to a person in the role models map.
	 *
	 * @param roleModelsMap - The map of role models
	 * @param role - The role to add (Owner, Resident, Tenant, or Vacancy)
	 * @param roleType - The type of the role
	 */
	addRoleToPerson(roleModelsMap: Map<string, RoleModel>, role: TenantUnitRoleDto, roleType: RoleType): void {
		if (!roleModelsMap || !role) {
			return;
		}

		const person = this.getOrCreatePerson(role, roleModelsMap.size);
		const roleData = this.createRoleData(role, roleType);

		const existingRoleModel = roleModelsMap.get(person.id);

		if (existingRoleModel) {
			existingRoleModel.roles.push(roleData);
		} else {
			roleModelsMap.set(person.id, this.createRoleModel(person, roleData, roleType));
		}
	}

	/**
	 * Sorts roles and marks overlapping time periods.
	 *
	 * @param roles - Array of roles to process
	 * @returns Sorted array with overlap flags set
	 */
	sortAndMarkOverlappingRoles(roles: Role[], vacancyPeriods: Array<{ start: Dayjs; end: Dayjs }> = []): Role[] {
		const sortedRoles = this.sortRolesByValidFrom(roles);

		this.resetOverlapFlags(sortedRoles);
		this.markOverlappingRoles(sortedRoles);
		this.markIncompleteRoles(sortedRoles, vacancyPeriods);

		return sortedRoles;
	}

	/**
	 * Checks if a role is currently active (no end date or end date is max date).
	 *
	 * @param role - The role to check
	 * @returns True if the role is currently active
	 */
	isCurrentRole(role: Role): boolean {
		const maxDate = DateUtil.maxDate;
		return role.validTo === undefined || dayjs(role.validTo).isSame(maxDate, 'days');
	}

	/**
	 * Checks if a role model has at least one current role.
	 *
	 * @param roleModel - The role model to check
	 * @returns True if at least one role is current
	 */
	hasCurrentRole(roleModel: RoleModel): boolean {
		return roleModel.roles.some((role) => this.isCurrentRole(role));
	}

	/**
	 * „Heute gültig": von ≤ heute ≤ bis (∞-tolerant). Im Gegensatz zu {@link isCurrentRole} (= offen-
	 * endig) zählt ein zukünftiger oder bereits beendeter Zeitraum NICHT als heute — für die führende,
	 * aufgeklappte Darstellung („zeige das Heutige, nicht das Neueste").
	 */
	isTodayRole(role: Role): boolean {
		return DateUtil.isValidToday(role.validFrom, role.validTo);
	}

	/** True, wenn mindestens eine Rolle des Modells heute gültig ist. */
	hasTodayRole(roleModel: RoleModel): boolean {
		return roleModel.roles.some((role) => this.isTodayRole(role));
	}

	/**
	 * Checks if a role type is selected based on filter configuration.
	 *
	 * @param roleType - The role type to check
	 * @param config - The filter configuration
	 * @returns True if the role type is selected
	 */
	isRoleTypeSelected(roleType: RoleType, config: RoleFilterConfig): boolean {
		switch (roleType) {
			case RoleTypeEnum.OWNER:
				return config.showOwner;
			case RoleTypeEnum.RESIDENT:
				return config.showResident;
			case RoleTypeEnum.TENANT:
				return config.showTenant;
			case RoleTypeEnum.VACANCY:
				return config.showVacancy;
			case UiRoleType.CONSUMPTION_INFO_RECEIVER:
				return config.showConsumptionInfoReceiver ?? true;
			default:
				return false;
		}
	}

	/**
	 * Checks if a role falls within the specified date range.
	 *
	 * @param role - The role to check
	 * @param globalValidFrom - Start of the date range (optional)
	 * @param globalValidTo - End of the date range (optional)
	 * @returns True if the role overlaps with the date range
	 */
	isRoleInDateRange(role: Role, globalValidFrom?: Date, globalValidTo?: Date): boolean {
		const { roleValidFrom, roleValidTo } = this.extractRoleDates(role);

		// Role without date limits is always valid
		if (!roleValidFrom && !roleValidTo) {
			return true;
		}

		return this.checkDateRangeOverlap(roleValidFrom, roleValidTo, globalValidFrom, globalValidTo);
	}

	/**
	 * Creates a filter predicate for role models based on date range and role type filters.
	 *
	 * @param config - The filter configuration
	 * @returns Filter predicate function
	 */
	createFilterPredicate(config: RoleFilterConfig): (data: RoleModel) => boolean {
		return (data: RoleModel) => {
			return data.roles.some((role) => {
				const isInDateRange = this.isRoleInDateRange(role, config.validFrom, config.validTo);
				const isRoleTypeSelected = this.isRoleTypeSelected(role.roleType, config);
				return isInDateRange && isRoleTypeSelected;
			});
		};
	}

	/**
	 * Gets sorting data accessor for role models.
	 *
	 * @param roleModel - The role model
	 * @param sortHeaderId - The column to sort by
	 * @returns The sort value
	 */
	getSortingValue(roleModel: RoleModel, sortHeaderId: string): string | number {
		switch (sortHeaderId) {
			case 'roleBeginDate':
				return this.getMaxValidFromValue(roleModel);
			case 'roleEndDate':
				return this.getMaxValidToValue(roleModel);
			case 'lastName':
				return roleModel.person?.familyName || (roleModel.isVacancy ? 'ZZZ' : '');
			case 'firstName':
				return roleModel.person?.givenName || (roleModel.isVacancy ? 'ZZZ' : '');
			default:
				return '';
		}
	}

	// ============ Private Methods ============

	private getSortedRoleData(
		tenantUnit: TenantUnitDetail,
		hideTenants: boolean
	): {
		owners: Owner[];
		residents: Resident[];
		tenants: Tenant[];
		vacancies: Vacancy[];
		consumptionInfoReceivers: ConsumptionInfoReceiver[];
	} {
		return {
			owners: this.sortByValidFrom(tenantUnit.owners),
			residents: this.sortByValidFrom(tenantUnit.residents),
			tenants: hideTenants ? [] : this.sortByValidFrom(tenantUnit.tenants),
			vacancies: this.sortByValidFrom(tenantUnit.vacancies),
			consumptionInfoReceivers: this.sortByValidFrom(tenantUnit.consumptionInfoReceivers ?? []),
		};
	}

	private sortByValidFrom<T extends { validFrom?: string | Date }>(items: T[]): T[] {
		return [...items].sort((a, b) => dayjs(a.validFrom).diff(dayjs(b.validFrom)));
	}

	private buildOccupiedPeriods(owners: Owner[], tenants: Tenant[]): TimePeriod[] {
		const ownerPeriods = owners.map((o) => ({
			start: dayjs(o.validFrom),
			end: dayjs(o.validTo),
		}));

		const tenantPeriods = tenants.map((t) => ({
			start: dayjs(t.validFrom),
			end: dayjs(t.validTo),
		}));

		return this.timePeriodService.sortPeriodsByStart([...ownerPeriods, ...tenantPeriods]);
	}

	private buildVacancyTimePeriods(vacancies: Vacancy[]): TimePeriod[] {
		return vacancies
			.map((v) => ({
				start: dayjs(v.validFrom),
				end: dayjs(v.validTo),
			}))
			.sort((a, b) => a.start.diff(b.start));
	}

	private buildRoleModelsMap(
		sortedData: {
			owners: Owner[];
			residents: Resident[];
			tenants: Tenant[];
			vacancies: Vacancy[];
			consumptionInfoReceivers: ConsumptionInfoReceiver[];
		},
		uncoveredVacancyGaps: TimePeriod[]
	): Map<string, RoleModel> {
		const roleModelsMap: Map<string, RoleModel> = new Map();

		for (const owner of sortedData.owners) {
			this.addRoleToPerson(roleModelsMap, owner, UiRoleType.OWNER);
		}
		for (const resident of sortedData.residents) {
			this.addRoleToPerson(roleModelsMap, resident, UiRoleType.RESIDENT);
		}
		for (const tenant of sortedData.tenants) {
			this.addRoleToPerson(roleModelsMap, tenant, UiRoleType.TENANT);
		}
		for (const vacancy of sortedData.vacancies) {
			this.addRoleToPerson(roleModelsMap, vacancy, UiRoleType.VACANCY);
		}
		for (const receiver of sortedData.consumptionInfoReceivers) {
			this.addRoleToPerson(roleModelsMap, receiver, UiRoleType.CONSUMPTION_INFO_RECEIVER);
		}

		// Add uncovered vacancy gaps as vacancy role models
		this.addUncoveredVacancyGaps(roleModelsMap, uncoveredVacancyGaps);

		return roleModelsMap;
	}

	private addUncoveredVacancyGaps(roleModelsMap: Map<string, RoleModel>, uncoveredVacancyGaps: TimePeriod[]): void {
		for (const vacancyGap of uncoveredVacancyGaps) {
			const vacancyRole = {
				validFrom: vacancyGap.start.format('YYYY-MM-DD'),
				validTo: vacancyGap.end.format('YYYY-MM-DD'),
			} as Vacancy;

			this.addRoleToPerson(roleModelsMap, vacancyRole, UiRoleType.VACANCY);
		}
	}

	private buildAvailablePeriodsByRoleType(tenantUnit: TenantUnitDetail, hideTenants: boolean): Map<RoleTypeEnum, TimePeriod[]> {
		const availablePeriodsByRoleType = new Map<RoleTypeEnum, TimePeriod[]>();

		availablePeriodsByRoleType.set(RoleTypeEnum.OWNER, this.calculateAvailablePeriodsForRoleType(tenantUnit.owners, tenantUnit.currentOwner));

		availablePeriodsByRoleType.set(RoleTypeEnum.RESIDENT, this.calculateAvailablePeriodsForRoleType(tenantUnit.residents, tenantUnit.currentResident));

		if (!hideTenants) {
			availablePeriodsByRoleType.set(RoleTypeEnum.TENANT, this.calculateAvailablePeriodsForRoleType(tenantUnit.tenants, tenantUnit.currentTenant));
		}

		return availablePeriodsByRoleType;
	}

	private calculateAvailablePeriodsForRoleType(
		roles: Array<{ validFrom?: string | Date; validTo?: string | Date }>,
		currentRole?: { validFrom?: string | Date; validTo?: string | Date }
	): TimePeriod[] {
		const occupiedPeriods: TimePeriod[] = roles.map((role) => ({
			start: dayjs(role.validFrom),
			end: dayjs(role.validTo),
		}));

		const sortedPeriods = this.timePeriodService.sortPeriodsByStart(occupiedPeriods);

		// Offene aktuelle Rolle: belegten Zeitraum auf start+1 kappen, damit der Rest wählbar bleibt.
		if (sortedPeriods.length > 0 && currentRole) {
			const lastPeriod = sortedPeriods.at(-1);
			lastPeriod.end = lastPeriod.start.add(1, 'day');
		}

		const mergedPeriods = this.timePeriodService.mergeTimePeriods(sortedPeriods);

		return this.timePeriodService.calculateAvailablePeriods(mergedPeriods, DateUtil.minDate, DateUtil.maxDate);
	}

	private getOrCreatePerson(role: TenantUnitRoleDto, mapSize: number): Person {
		let person: Person = role.person;

		if (!person) {
			person = { id: SYNTHETIC_VACANCY_ID_PREFIX + mapSize };
		} else if (!person.id) {
			person.id = SYNTHETIC_NEW_ID_PREFIX + mapSize;
		}

		return person;
	}

	private createRoleData(role: TenantUnitRoleDto, roleType: RoleType): Role {
		return {
			id: role.id,
			roleType,
			validFrom: dayjs(role.validFrom),
			validTo: dayjs(role.validTo),
			propertyManagementNumber: role.propertyManagementNumber,
		};
	}

	private createRoleModel(person: Person, roleData: Role, roleType: RoleType): RoleModel {
		return {
			person,
			isHighlighting: false,
			isVacancy: roleType === RoleTypeEnum.VACANCY,
			roles: [roleData],
		};
	}

	private sortRolesByValidFrom(roles: Role[]): Role[] {
		return [...roles].sort((a, b) => {
			if (!a.validFrom && !b.validFrom) return 0;
			if (!a.validFrom) return 1;
			if (!b.validFrom) return -1;
			return b.validFrom.valueOf() - a.validFrom.valueOf();
		});
	}

	private resetOverlapFlags(roles: Role[]): void {
		for (const role of roles) {
			role.isOverlappingValidFrom = false;
			role.isOverlappingValidTo = false;
		}
	}

	private markOverlappingRoles(roles: Role[]): void {
		for (let i = 0; i < roles.length; i++) {
			const current = roles[i];

			if (!current.validFrom || !current.validTo) continue;

			for (let j = 0; j < roles.length; j++) {
				if (i === j) continue;

				const other = roles[j];

				if (!other.validFrom || !other.validTo) continue;

				this.checkAndMarkOverlap(current, other);
			}
		}
	}

	private checkAndMarkOverlap(current: Role, other: Role): void {
		const currentValidFrom = current.validFrom;
		const currentValidTo = current.validTo;
		const otherValidFrom = other.validFrom;
		const otherValidTo = other.validTo;

		// Check if validFrom of current is within the range of other
		if (this.isDateInRange(currentValidFrom, otherValidFrom, otherValidTo)) {
			current.isOverlappingValidFrom = true;
		}

		// Check if validTo of current is within the range of other
		if (this.isDateInRange(currentValidTo, otherValidFrom, otherValidTo)) {
			current.isOverlappingValidTo = true;
		}

		// Check if current completely encloses other
		if (this.doesPeriodEnclose(currentValidFrom, currentValidTo, otherValidFrom, otherValidTo)) {
			current.isOverlappingValidFrom = true;
			current.isOverlappingValidTo = true;
		}
	}

	private isDateInRange(date: Dayjs, rangeStart: Dayjs, rangeEnd: Dayjs): boolean {
		return (date.isAfter(rangeStart) || date.isSame(rangeStart)) && (date.isBefore(rangeEnd) || date.isSame(rangeEnd));
	}

	private doesPeriodEnclose(start1: Dayjs, end1: Dayjs, start2: Dayjs, end2: Dayjs): boolean {
		return (start1.isBefore(start2) || start1.isSame(start2)) && (end1.isAfter(end2) || end1.isSame(end2));
	}

	private markIncompleteRoles(roles: Role[], vacancyPeriods: Array<{ start: Dayjs; end: Dayjs }>): void {
		for (let i = 0; i < roles.length - 1; i++) {
			const current = roles[i];
			const next = roles[i + 1];

			if (!current.validFrom || !current.validTo || !next.validFrom || !next.validTo) continue;

			const daysDifference = current.validFrom.diff(next.validTo, 'day');

			if (daysDifference > 1) {
				const gapStart = next.validTo.add(1, 'day');
				const gapEnd = current.validFrom.subtract(1, 'day');
				if (this.isGapCoveredByVacancy(gapStart, gapEnd, vacancyPeriods)) {
					continue;
				}
				current.isIncompleteValidFrom = true;
				next.isIncompleteValidTo = true;
			}
		}
	}

	private isGapCoveredByVacancy(gapStart: Dayjs, gapEnd: Dayjs, vacancyPeriods: Array<{ start: Dayjs; end: Dayjs }>): boolean {
		if (!vacancyPeriods.length || gapStart.isAfter(gapEnd)) {
			return false;
		}

		const sorted = [...vacancyPeriods].sort((a, b) => a.start.diff(b.start));
		let coveredUntil: Dayjs | null = null;

		for (const period of sorted) {
			if (!period?.start || !period?.end) {
				continue;
			}

			const start = period.start.startOf('day');
			const end = period.end.startOf('day');

			if (end.isBefore(gapStart) || start.isAfter(gapEnd)) {
				continue;
			}

			if (coveredUntil === null) {
				if (start.isAfter(gapStart)) {
					return false;
				}
				coveredUntil = end;
			} else if (start.isAfter(coveredUntil.add(1, 'day'))) {
				return false;
			} else if (end.isAfter(coveredUntil)) {
				coveredUntil = end;
			}

			if (coveredUntil.isSame(gapEnd) || coveredUntil.isAfter(gapEnd)) {
				return true;
			}
		}

		return false;
	}

	private extractRoleDates(role: Role): { roleValidFrom: Date | null; roleValidTo: Date | null } {
		return { roleValidFrom: this.toDateOrNull(role.validFrom), roleValidTo: this.toDateOrNull(role.validTo) };
	}

	/** Rollen-Datumswerte können historisch Dayjs oder ISO-String sein. */
	private toDateOrNull(value: Dayjs | string | undefined): Date | null {
		if (isDayjs(value)) {
			return value.toDate();
		}
		return value ? new Date(value) : null;
	}

	private checkDateRangeOverlap(roleValidFrom: Date | null, roleValidTo: Date | null, globalValidFrom?: Date, globalValidTo?: Date): boolean {
		// Only globalValidFrom set
		if (globalValidFrom && !globalValidTo) {
			return !(roleValidTo && roleValidTo < globalValidFrom);
		}

		// Only globalValidTo set
		if (!globalValidFrom && globalValidTo) {
			return !(roleValidFrom && roleValidFrom > globalValidTo);
		}

		// Both filters set
		if (globalValidFrom && globalValidTo) {
			if (roleValidTo && roleValidTo < globalValidFrom) {
				return false;
			}
			if (roleValidFrom && roleValidFrom > globalValidTo) {
				return false;
			}
			return true;
		}

		return true;
	}

	private getMaxValidFromValue(roleModel: RoleModel): number {
		if (!roleModel.roles || roleModel.roles.length === 0) {
			return 0;
		}

		const dates = roleModel.roles.map((role) => role.validFrom);
		const maxDate = dayjs.max(dates);

		return maxDate ? maxDate.valueOf() : 0;
	}

	private getMaxValidToValue(roleModel: RoleModel): number {
		if (!roleModel.roles || roleModel.roles.length === 0) {
			return 0;
		}

		const hasNull = roleModel.roles.some((role) => role.validTo === null || role.validTo === undefined);

		if (hasNull) {
			return Number.MAX_SAFE_INTEGER;
		}

		const dates = roleModel.roles.map((role) => role.validTo);
		const maxDate = dayjs.max(dates);

		return maxDate ? maxDate.valueOf() : 0;
	}
}
